import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import AboutFormFields, { getEmptyAboutFormValues } from "./about_form_fields";
import {
  AboutLeader,
  buildAboutDesignation,
  defaultAboutLeaders,
  normalizeAboutLeader,
  sortAboutLeaders,
} from "../../about/about_data";
import { toTitleCaseName } from "../../../utils/title_case";

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

async function resequenceAboutLeaders(targetLeaderId?: string) {
  const snapshot = await getDocs(collection(firestore, "about_leadership"));
  const orderedDocs = snapshot.docs.sort((docA, docB) => {
    const rankA = Number(docA.data().rank || 0);
    const rankB = Number(docB.data().rank || 0);

    if (rankA !== rankB) {
      return rankA - rankB;
    }

    if (targetLeaderId && docA.id === targetLeaderId) {
      return -1;
    }

    if (targetLeaderId && docB.id === targetLeaderId) {
      return 1;
    }

    return String(docA.data().name || "").localeCompare(String(docB.data().name || ""));
  });

  const batch = writeBatch(firestore);
  const timestamp = new Date().toISOString();

  orderedDocs.forEach((leaderDoc, index) => {
    batch.update(leaderDoc.ref, {
      rank: index + 1,
      updatedAt: timestamp,
    });
  });

  await batch.commit();
}

export default function ManageAboutLeadersTab() {
  const { showSnackbar } = useFeedback();
  const [leaders, setLeaders] = useState<AboutLeader[]>([]);
  const [editingLeader, setEditingLeader] = useState<AboutLeader | null>(null);
  const [formValues, setFormValues] = useState(getEmptyAboutFormValues());
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  async function seedDefaultLeaders() {
    const timestamp = new Date().toISOString();

    await Promise.all(
      defaultAboutLeaders.map((leader) =>
        setDoc(
          doc(
            firestore,
            "about_leadership",
            `${leader.rank}-${leader.designationType}-${leader.name}`
              .replace(/[^a-zA-Z0-9]+/g, "-")
              .toLowerCase(),
          ),
          {
            ...leader,
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        ),
      ),
    );
  }

  async function fetchLeaders() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(collection(firestore, "about_leadership"));

      if (!snapshot.docs.length) {
        await seedDefaultLeaders();
        const seededSnapshot = await getDocs(collection(firestore, "about_leadership"));
        setLeaders(
          sortAboutLeaders(
            seededSnapshot.docs.map((leaderDoc) =>
              normalizeAboutLeader({
                docId: leaderDoc.id,
                ...(leaderDoc.data() as Omit<AboutLeader, "docId">),
              }),
            ),
          ),
        );
        return;
      }

      setLeaders(
        sortAboutLeaders(
          snapshot.docs.map((leaderDoc) =>
            normalizeAboutLeader({
              docId: leaderDoc.id,
              ...(leaderDoc.data() as Omit<AboutLeader, "docId">),
            }),
          ),
        ),
      );
    } catch (error) {
      console.error("Failed to load about leaders", error);
      showSnackbar("Failed to load leaders");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchLeaders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEdit(leader: AboutLeader) {
    setEditingLeader(leader);
    setFormValues({
      rank: String(leader.rank),
      name: leader.name,
      designationType: leader.designationType,
      designationDetail: leader.designationDetail || "",
      place: leader.place || "",
      img: leader.img || "",
    });
    setSelectedImage(null);
  }

  async function handleUpdate() {
    if (!editingLeader?.docId) {
      return;
    }

    const rank = Number(formValues.rank);

    if (!formValues.name.trim() || !Number.isInteger(rank) || rank <= 0) {
      showSnackbar("Please fill rank and name");
      return;
    }

    try {
      setIsSaving(true);
      let finalImage = formValues.img.trim();

      if (selectedImage) {
        const storageRef = ref(storage, `about_leadership/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      const timestamp = new Date().toISOString();

      await updateDoc(doc(firestore, "about_leadership", editingLeader.docId), {
        rank,
        name: toTitleCaseName(formValues.name),
        designationType: formValues.designationType,
        designationDetail: formValues.designationDetail.trim(),
        designation: buildAboutDesignation(
          formValues.designationType,
          formValues.designationDetail,
        ),
        place: formValues.place.trim(),
        img: normalizeUrl(finalImage),
        updatedAt: timestamp,
      });

      await resequenceAboutLeaders(editingLeader.docId);

      showSnackbar("Leader updated successfully");
      setEditingLeader(null);
      setFormValues(getEmptyAboutFormValues());
      setSelectedImage(null);
      fetchLeaders();
    } catch (error) {
      console.error("Failed to update leader", error);
      showSnackbar("Failed to update leader");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(leader: AboutLeader) {
    if (!leader.docId) {
      return;
    }

    if (!window.confirm(`Delete "${leader.name}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, "about_leadership", leader.docId));
      await resequenceAboutLeaders();
      showSnackbar("Leader deleted successfully");
      fetchLeaders();
    } catch (error) {
      console.error("Failed to delete leader", error);
      showSnackbar("Failed to delete leader");
    }
  }

  if (isLoading) {
    return (
      <Box py={6} textAlign="center">
        <Typography>Loading leaders...</Typography>
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        {leaders.map((leader) => (
          <Grid item xs={12} sm={6} md={4} key={leader.docId || `${leader.rank}-${leader.name}`}>
            <Card sx={{ height: "100%" }}>
              {leader.img ? (
                <Box
                  component="img"
                  src={leader.img}
                  alt={leader.name}
                  sx={{
                    width: "100%",
                    height: 240,
                    objectFit: "cover",
                    objectPosition: "center top",
                  }}
                />
              ) : null}
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Chip label={`Rank ${leader.rank}`} color="primary" size="small" />
                </Box>
                <Typography variant="h6" fontWeight={700}>
                  {leader.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {leader.designation}
                </Typography>
                {leader.place ? (
                  <Typography variant="body2" color="text.secondary">
                    {leader.place}
                  </Typography>
                ) : null}
                <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(leader)}>
                    Edit
                  </Button>
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(leader)}>
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingLeader)} onClose={() => setEditingLeader(null)} fullWidth maxWidth="md">
        <DialogTitle>Edit Leader</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} mt={0.5}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: "none", border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                <CardContent sx={{ p: 3 }}>
                  <AboutFormFields formValues={formValues} setFormValues={setFormValues} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: "none", border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" flexDirection="column" gap={2.5}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={formValues.img}
                      onChange={(event) => {
                        setFormValues((prev) => ({ ...prev, img: event.target.value }));
                        if (event.target.value) {
                          setSelectedImage(null);
                        }
                      }}
                    />
                    <FormControl fullWidth>
                      <FormLabel>Upload New Leader Image</FormLabel>
                      <input
                        type="file"
                        accept="image/*"
                        style={{
                          border: "1px solid rgb(181, 176, 176)",
                          width: "96%",
                          paddingLeft: 8,
                          paddingRight: 8,
                          paddingTop: 16,
                          paddingBottom: 16,
                          borderRadius: 5,
                          marginTop: 8,
                        }}
                        onChange={(event) => {
                          if (event.target.files?.[0]) {
                            setSelectedImage(event.target.files[0]);
                            setFormValues((prev) => ({ ...prev, img: "" }));
                          }
                        }}
                      />
                      {selectedImage ? (
                        <Box mt={2} position="relative" display="inline-block">
                          <img
                            src={URL.createObjectURL(selectedImage)}
                            alt="Updated leader preview"
                            style={{
                              maxWidth: "220px",
                              maxHeight: "220px",
                              borderRadius: 8,
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setSelectedImage(null)}
                            style={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              color: "#ffffff",
                              backgroundColor: "rgba(0,0,0,0.5)",
                            }}
                          >
                            <Close />
                          </IconButton>
                        </Box>
                      ) : formValues.img ? (
                        <Box mt={2}>
                          <Box
                            component="img"
                            src={formValues.img}
                            alt="Current leader"
                            sx={{
                              maxWidth: 220,
                              maxHeight: 220,
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                      ) : null}
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingLeader(null)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleUpdate} variant="contained" disabled={isSaving}>
            {isSaving ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
